import {
  useCloneIntegration,
  useQueryPipelines,
  useQueryIntegrationNames,
  useLazyQuery,
  useMutation,
  integrationPlaybooksQuery,
  insertPipelineIntegrationMutation,
  buildInsertPipelineIntegrationVariables,
} from '@ion/api';
import useUpsertIntegrationNotes from '@ion/api/integrations/useUpsertIntegrationNotes';
import permissions from '@ion/api/Apollo/permissions';

export default function useCloneIntegrationModal({ integration, onClose }) {
  const [cloneIntegration, { loading }] = useCloneIntegration();
  const { data: pipelines } = useQueryPipelines();
  const { integrations } = useQueryIntegrationNames();

  const [fetchPlaybooks] = useLazyQuery(integrationPlaybooksQuery, {
    context: { permission: permissions.readIntegration },
    fetchPolicy: 'network-only',
  });

  const [upsertNotes] = useUpsertIntegrationNotes();

  const [executePipelineIntegrationMutation] = useMutation(insertPipelineIntegrationMutation, {
    context: { permission: permissions.createPipelineIntegration },
  });

  const existingNames = integrations.map(integrationItem => integrationItem.name);
  const defaultName = `${integration.name} (Copy)`;

  // Orchestrates entire clone integration process
  // fetch all playbooks for integration
  // clone integration with all historical playbooks
  // upsert current notes to the cloned integration
  // create pipeline connection for pipelines configured
  const onSubmit = async formData => {
    const { name, pipelineIds } = formData;

    const playbooksResult = await fetchPlaybooks({ variables: { integrationId: integration.id } });
    const playbooks = playbooksResult.data?.integrations_by_pk?.playbooks ?? [];

    const cloneResult = await cloneIntegration({
      newName: name,
      starterkitId: integration.starterkitId,
      starterkitVersion: integration.starterkitVersion,
      playbooks,
    });

    if (cloneResult.errors) {
      return;
    }

    const newIntegration = cloneResult.data?.insert_integrations_one;
    const newIntegrationId = newIntegration?.id;
    const notes = integration.integration_notes?.notes;

    if (notes && newIntegrationId) {
      await upsertNotes({ notes, integrationId: newIntegrationId });
    }

    const newPlaybookId = newIntegration?.playbooks?.[0]?.id;
    const ids = pipelineIds ?? [];

    if (ids.length > 0 && newPlaybookId && newIntegrationId) {
      const pipelineInsertPromises = ids.map(pipelineId =>
        executePipelineIntegrationMutation({
          variables: buildInsertPipelineIntegrationVariables({
            pipelineId,
            integrationId: newIntegrationId,
            playbookId: newPlaybookId,
            pipelineVars: {},
            readyForDeploy: false,
          }),
        })
      );

      await Promise.all(pipelineInsertPromises);
    }

    onClose();
  };

  return {
    defaultName,
    pipelines: pipelines ?? [],
    existingNames,
    onClose,
    onSubmit,
    loading,
  };
}
